// ---------------------------------------------------------------------------
// applyTerminologyScopeSelection — writes a terminology-selection pick to the
// current project's declared scope.
//
// Plan: docs/superpowers/plans/terminology-selection-ui-plan.md (Task 3).
//
// Wired from the terminology-selection card's onSelect
// (shell/ide-v2/components/chat-messages.tsx). Self-extinguishing +
// fail-open (plan §Global Constraints): the in-memory store updates
// immediately so the very next decompose already carries the scope even if
// the network write below is slow or never lands; a failed persist never
// throws — the caller gets a structured result and is expected to surface
// its own non-fatal notice while the in-memory scope keeps applying for the
// rest of the session.
// ---------------------------------------------------------------------------

import { useStudioStore } from '@/store/studio-store'
import { persistProjectTerminologyScope } from '@/services/project-api'

// Shared with app-spec-from-prompt.ts's read-side counterpart
// (currentTerminologyScope there imports this constant and applies the
// identical check before forwarding a declared scope to decompose).
// Authoritative server-side counterpart:
// functions/api/knowledge/terminology-diagnosis.ts's `APPLICATION_SCOPE_RE`
// (kept in lockstep by comment there, not by import — functions/ is a
// separate bundle boundary from src/).
export const TERMINOLOGY_SCOPE_PATTERN = /^app:[a-z0-9-]{1,64}$/

export function isValidTerminologyScope(scope: string): boolean {
  return scope === 'platform' || TERMINOLOGY_SCOPE_PATTERN.test(scope)
}

/**
 * The open project DECLARES its scope (never synthesized/derived here).
 * No open project or no declaration means no scope is sent — JSON.stringify
 * drops undefined, so generate/decompose bodies stay byte-identical.
 */
export function currentTerminologyScope(): string | undefined {
  const declared = useStudioStore.getState().projectMeta?.terminologyScope
  if (declared === 'platform' || (declared && TERMINOLOGY_SCOPE_PATTERN.test(declared)))
    return declared
  return undefined
}

export type ApplyTerminologyScopeResult =
  | { ok: true }
  | { ok: false; reason: 'no-project' | 'invalid-scope' | 'persist-failed' | 'scope-conflict' }

/**
 * Declare `scope` (verbatim from a TerminologySelectionTerm/Candidate —
 * never free text) as the open project's terminology scope.
 *
 * - No open project → `{ ok: false, reason: 'no-project' }`, no write at
 *   all (nothing to declare a scope on; covers the edge where the project
 *   closed between the card being appended and the user clicking it).
 * - Malformed scope → `{ ok: false, reason: 'invalid-scope' }`, no write.
 *   Defense-in-depth only: every scope this function is ever called with
 *   comes from the diagnosis, which already validated it server-side.
 * - Project already declares a DIFFERENT `app:...` scope →
 *   `{ ok: false, reason: 'scope-conflict' }`, no write at all. C2's
 *   bootstrap-only surfacing makes this near-impossible in the normal
 *   flow (a project with a declared scope never bootstrap-searches other
 *   scopes — terminology-diagnosis.ts), but a stale card (built against an
 *   older diagnosis, before the user's project gained a declaration) could
 *   still try to fire this call — refuse rather than silently replacing an
 *   established declaration out from under the user.
 * - Otherwise: the in-memory store updates synchronously, THEN the pick is
 *   persisted. A persist failure resolves `{ ok: false, reason:
 *   'persist-failed' }` without throwing and without rolling back the
 *   in-memory value — it still applies for the rest of this session.
 */
export async function applyTerminologyScopeSelection(
  scope: string,
): Promise<ApplyTerminologyScopeResult> {
  if (!isValidTerminologyScope(scope)) return { ok: false, reason: 'invalid-scope' }

  const state = useStudioStore.getState()
  const projectId = state.currentProjectId
  const projectMeta = state.projectMeta
  if (!projectId || !projectMeta) return { ok: false, reason: 'no-project' }

  const existingScope = (projectMeta as { terminologyScope?: string }).terminologyScope
  if (
    existingScope &&
    existingScope !== 'platform' &&
    existingScope !== scope
  ) {
    return { ok: false, reason: 'scope-conflict' }
  }

  // Immediate in-memory update — app-spec-from-prompt.ts's
  // currentTerminologyScope() reads this on the very next decompose call,
  // so the pick applies for the rest of the session even if the persist
  // below is slow or never lands.
  useStudioStore.setState({
    projectMeta: { ...projectMeta, terminologyScope: scope },
  } as never)

  try {
    await persistProjectTerminologyScope(projectId, scope)
    return { ok: true }
  } catch {
    return { ok: false, reason: 'persist-failed' }
  }
}
